/**
 * Error Reporting Service
 * Automatically reports errors to the support system with deduplication.
 *
 * Features:
 * - Captures screenshots using html2canvas
 * - Deduplication: 1 ticket per module/jsx/page per day
 * - Only active in production mode
 * - Includes user context if logged in
 */

import html2canvas from 'html2canvas';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/support`;

// Storage key for deduplication tracking
const ERROR_TRACKING_KEY = 'cabaza_error_reports';

/**
 * Get today's date as YYYY-MM-DD string
 */
const getTodayKey = () => {
    return new Date().toISOString().split('T')[0];
};

/**
 * Generate a unique key for error deduplication
 * @param {string} module - Module name
 * @param {string} componentName - JSX component name
 * @param {string} page - Current page/route
 */
const getErrorKey = (module, componentName, page) => {
    return `${module}|${componentName}|${page}`;
};

/**
 * Check if this error was already reported today
 * @param {string} errorKey - Unique error identifier
 * @returns {boolean} True if already reported
 */
const wasReportedToday = (errorKey) => {
    try {
        const tracking = JSON.parse(localStorage.getItem(ERROR_TRACKING_KEY) || '{}');
        const today = getTodayKey();

        // Clean up old entries (keep only today)
        if (tracking.date !== today) {
            localStorage.setItem(ERROR_TRACKING_KEY, JSON.stringify({ date: today, errors: {} }));
            return false;
        }

        return tracking.errors && tracking.errors[errorKey] === true;
    } catch {
        return false;
    }
};

/**
 * Mark error as reported for today
 * @param {string} errorKey - Unique error identifier
 */
const markAsReported = (errorKey) => {
    try {
        const today = getTodayKey();
        let tracking = JSON.parse(localStorage.getItem(ERROR_TRACKING_KEY) || '{}');

        if (tracking.date !== today) {
            tracking = { date: today, errors: {} };
        }

        tracking.errors[errorKey] = true;
        localStorage.setItem(ERROR_TRACKING_KEY, JSON.stringify(tracking));
    } catch {
        // Silently fail
    }
};

/**
 * Capture screenshot of the current page
 * @returns {Promise<string|null>} Base64 encoded screenshot or null
 */
export const captureScreenshot = async () => {
    try {
        const canvas = await html2canvas(document.body, {
            useCORS: true,
            allowTaint: true,
            logging: false,
            scale: 0.5, // Reduce size for faster capture
            ignoreElements: (element) => {
                // Ignore password fields and sensitive elements
                return element.type === 'password' || element.dataset.sensitiveData === 'true';
            }
        });

        return canvas.toDataURL('image/jpeg', 0.7);
    } catch (error) {
        console.error('Failed to capture screenshot:', error);
        return null;
    }
};

/**
 * Extract component name from error info
 * @param {Error} error - The error object
 * @param {Object} errorInfo - React error info with componentStack
 * @returns {string} Component name
 */
const extractComponentName = (error, errorInfo) => {
    if (errorInfo?.componentStack) {
        // Try to extract first component from stack
        const match = errorInfo.componentStack.match(/at (\w+)/);
        if (match) {
            return match[1];
        }
    }

    // Try to extract from error stack
    if (error?.stack) {
        const match = error.stack.match(/at (\w+)/);
        if (match) {
            return match[1];
        }
    }

    return 'Unknown';
};

/**
 * Detect current module from URL path
 * @returns {string} Module name
 */
const detectModule = () => {
    const path = window.location.pathname;

    // Map paths to modules
    const moduleMap = {
        '/contacts': 'contacts',
        '/contact/': 'contacts',
        '/people': 'people',
        '/calendar': 'calendar',
        '/messages': 'messages',
        '/profile': 'profile',
        '/support': 'support',
        '/work': 'work',
        '/offers': 'offers',
        '/endroits': 'places',
        '/endroit/': 'places',
        '/occupations': 'occupations',
        '/advertisements': 'advertisements',
        '/websites': 'websites',
        '/dashboard': 'dashboard',
        '/books': 'books',
        '/carpooling': 'carpooling',
        '/moments': 'moments',
        '/': 'home'
    };

    for (const [key, module] of Object.entries(moduleMap)) {
        if (path.startsWith(key)) {
            return module;
        }
    }

    return 'unknown';
};

/**
 * Get current user context if logged in
 * @returns {Object|null} User info or null
 */
const getUserContext = () => {
    try {
        const userStr = localStorage.getItem('cabaza_user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return {
                userId: user.id || user.ID || user.peopleId || user.PEOPLEID,
                email: user.email || user.EMAIL,
                firstName: user.firstName || user.FIRSTNAME,
                lastName: user.lastName || user.LASTNAME
            };
        }
    } catch {
        // Silently fail
    }
    return null;
};

/**
 * Report an error to the support system
 * @param {Error} error - The error object
 * @param {Object} errorInfo - React error info (optional)
 * @param {Object} options - Additional options
 * @param {string} options.source - Error source (react, global, promise)
 * @returns {Promise<boolean>} True if reported successfully
 */
export const reportError = async (error, errorInfo = null, options = {}) => {
    // Only report in production
    if (!import.meta.env.PROD) {
        console.log('[ErrorReporting] Skipped in development mode');
        return false;
    }

    try {
        const module = detectModule();
        const componentName = extractComponentName(error, errorInfo);
        const currentPage = window.location.pathname;
        const errorKey = getErrorKey(module, componentName, currentPage);

        // Check deduplication
        if (wasReportedToday(errorKey)) {
            console.log('[ErrorReporting] Error already reported today:', errorKey);
            return false;
        }

        // Get user context
        const user = getUserContext();

        // Capture screenshot
        const screenshot = await captureScreenshot();

        // Build error report
        const errorReport = {
            module,
            componentName,
            page: currentPage,
            errorMessage: error?.message || String(error),
            errorStack: error?.stack || '',
            componentStack: errorInfo?.componentStack || '',
            source: options.source || 'unknown',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            screenshot,
            // User context if available
            userId: user?.userId || null,
            userEmail: user?.email || null,
            userName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : null
        };

        // Send to API
        const response = await fetch(`${API_BASE_URL}/error-report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(errorReport)
        });

        if (response.ok) {
            markAsReported(errorKey);
            console.log('[ErrorReporting] Error reported successfully');
            return true;
        } else {
            console.error('[ErrorReporting] Failed to report error:', response.status);
            return false;
        }
    } catch (reportError) {
        console.error('[ErrorReporting] Error while reporting:', reportError);
        return false;
    }
};

/**
 * Setup global error handlers
 * Call this once at app initialization
 */
export const setupGlobalErrorHandlers = () => {
    // Only in production
    if (!import.meta.env.PROD) {
        console.log('[ErrorReporting] Global handlers not installed in development mode');
        return;
    }

    // Handle uncaught errors
    window.onerror = (message, source, lineno, colno, error) => {
        reportError(error || new Error(String(message)), null, { source: 'global' });
        // Don't prevent default handling
        return false;
    };

    // Handle unhandled promise rejections
    window.onunhandledrejection = (event) => {
        const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason));
        reportError(error, null, { source: 'promise' });
    };

    console.log('[ErrorReporting] Global error handlers installed');
};

/**
 * Manually report an error (for use in catch blocks)
 * @param {Error} error - The error to report
 * @param {string} context - Additional context about where the error occurred
 */
export const manualReport = (error, context = '') => {
    const enrichedError = new Error(`${context ? `[${context}] ` : ''}${error.message}`);
    enrichedError.stack = error.stack;
    reportError(enrichedError, null, { source: 'manual' });
};
